import * as React from "react";
import { formatRounded } from "../../../utils/Format";
import { getID } from "../../../utils/IdGenerator";

type Props = {
	label?: string;
	required?: boolean;
	classes?: string;
	value?: number | null;
	onChange: (value: number | null) => void;
	min?: number;
	max?: number;
	step?: number;
	dp?: number;
	placeholder?: string;
	readOnly?: boolean;
	onBlur?: () => void;
};

type State = {
	id: string;
};

/**
 * Input for numbers. Uncontrolled to allow leading "-" and "+" characters .
 */
class NumberInput extends React.Component<Props, State> {
	private readonly input: React.RefObject<HTMLInputElement>;
	constructor(props: Props) {
		super(props);
		this.input = React.createRef();
		this.state = {
			id: getID()
		};
	}

	public componentDidUpdate() {
		// Walkie-talkie (half-duplex) binding - don't take new values from React while the
		// field has the focus (otherwise snapping/rounding make it hard to type)
		if (this.input.current !== document.activeElement) {
			this.input.current!.value = this.toString(this.props.value);
		}
	}

	public onChange = () => {
		const num = parseFloat(this.input.current!.value);
		this.props.onChange(isNaN(num) ? null : num);
	};

	public focus = () => this.input.current!.focus();

	public render() {
		return (
			<div
				className={
					"form-item form-group form-item form-type-textfield form-group " +
					this.props.classes
				}
			>
				<input
					ref={this.input}
					defaultValue={this.toString(this.props.value)}
					required={this.props.required}
					className={`form-control ${this.props.required ? "required" : ""}`}
					type="number"
					id={this.state.id}
					min={this.props.min}
					max={this.props.max}
					step={this.props.step}
					placeholder={this.props.placeholder}
					readOnly={this.props.readOnly}
					onBlur={this.onBlur}
					onChange={this.onChange}
				/>
				{this.props.label && (
					<label className="control-label" htmlFor={this.state.id}>
						{this.props.label}
						{this.props.required === true && (
							<span className="form-required" title="This field is required" aria-hidden>
								*
							</span>
						)}
					</label>
				)}
			</div>
		);
	}

	private readonly onBlur = () => {
		// Now we can take the latest value from React (which may be snapped etc)
		this.input.current!.value = this.toString(this.props.value);
		this.props.onBlur?.call(this.props);
	};

	private readonly toString = (num?: number | null) =>
		num == null ? "" : formatRounded(num, this.props.dp ?? 2);
}

export default NumberInput;
